import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TextField, Button, CircularProgress, IconButton, Menu, MenuItem } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Mail, Lock, Eye, EyeOff, Globe, User, Building, Phone } from 'lucide-react';
import api from '../../services/api';
import { useLanguage } from '../../i18n/useLanguage';
import { languageNames } from '../../i18n/translations';
import NeuralBrain3D from '../../components/NeuralBrain3D';

const useStyles = makeStyles((theme) => ({
  '@keyframes floatIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(50px) scale(0.9)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  container: {
    position: 'relative',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    background: '#000000',
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
  },
  languageSelector: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 10,
  },
  languageButton: {
    color: '#00d9ff',
    background: 'rgba(0, 217, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(0, 217, 255, 0.3)',
    '&:hover': {
      background: 'rgba(0, 217, 255, 0.2)',
      borderColor: 'rgba(0, 217, 255, 0.5)',
    },
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
  signupCard: {
    background: 'rgba(0, 20, 40, 0.85)',
    backdropFilter: 'blur(40px) saturate(180%)',
    borderRadius: '20px',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(rgba(0, 20, 40, 0.85), rgba(0, 20, 40, 0.85)), linear-gradient(135deg, #00d9ff, #00ff88, #0099ff, #00ffff)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    boxShadow: '0 20px 60px rgba(0, 217, 255, 0.3), 0 0 40px rgba(0, 255, 136, 0.2)',
    padding: theme.spacing(3),
    width: '100%',
    maxWidth: '440px',
    position: 'relative',
    overflow: 'visible',
    margin: 'auto',
    animation: '$floatIn 0.8s ease-out',
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5),
      maxWidth: '95%',
      borderRadius: '16px',
    },
    '&:hover': {
      transform: 'translateY(-5px) scale(1.02)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(0, 255, 136, 0.5), transparent)',
    },
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  logoIcon: {
    width: '48px',
    height: '48px',
    background: 'linear-gradient(135deg, #00d9ff 0%, #00ff88 100%)',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(0, 217, 255, 0.5)',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      width: '42px',
      height: '42px',
      borderRadius: '12px',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '-2px',
      borderRadius: '18px',
      padding: '2px',
      background: 'linear-gradient(135deg, #00d9ff, #00ff88)',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      animation: '$pulse 2s ease-in-out infinite',
    },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #00d9ff 0%, #00ff88 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: theme.spacing(0.5),
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.35rem',
    },
  },
  subtitle: {
    color: 'rgba(0, 217, 255, 0.8)',
    fontSize: '0.85rem',
    fontWeight: 400,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.2),
    },
  },
  inputWrapper: {
    position: 'relative',
  },
  input: {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(0, 20, 40, 0.5)',
      borderRadius: '10px',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: 'rgba(0, 217, 255, 0.3)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 217, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00d9ff',
        borderWidth: '2px',
        boxShadow: '0 0 20px rgba(0, 217, 255, 0.3)',
      },
      '& input': {
        color: '#ffffff',
        padding: '10px 12px',
        fontSize: '0.9rem',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(0, 217, 255, 0.7)',
      fontSize: '0.9rem',
      transform: 'translate(42px, 12px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
        background: 'rgba(0, 20, 40, 0.9)',
        padding: '0 8px',
      },
      '&.Mui-focused': {
        color: '#00d9ff',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        '& input': {
          padding: '8px 10px',
          fontSize: '0.85rem',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.85rem',
        transform: 'translate(38px, 10px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      },
    },
  },
  button: {
    marginTop: theme.spacing(1),
    padding: '10px',
    background: 'linear-gradient(135deg, #00d9ff 0%, #00ff88 100%)',
    color: '#000000',
    fontWeight: 600,
    fontSize: '0.95rem',
    textTransform: 'none',
    borderRadius: '10px',
    border: 'none',
    boxShadow: '0 8px 24px rgba(0, 217, 255, 0.5)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      background: 'linear-gradient(135deg, #00b8d4 0%, #00e676 100%)',
      boxShadow: '0 12px 32px rgba(0, 217, 255, 0.6)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '&:disabled': {
      background: 'rgba(51, 65, 85, 0.5)',
      boxShadow: 'none',
      color: 'rgba(255, 255, 255, 0.3)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '8px',
      fontSize: '0.9rem',
    },
  },
  eyeIcon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: 'rgba(0, 217, 255, 0.7)',
    transition: 'color 0.2s',
    '&:hover': {
      color: '#00d9ff',
    },
  },
  alert: {
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    '& .MuiAlert-message': {
      color: '#fca5a5',
    },
  },
  footer: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    color: 'rgba(0, 217, 255, 0.7)',
    fontSize: '0.8rem',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.5),
      fontSize: '0.75rem',
    },
  },
  link: {
    color: '#00ff88',
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.2s',
    '&:hover': {
      color: '#00d9ff',
      textDecoration: 'underline',
    },
  },
}));

// Selector de idioma
function LanguageSelector({ language, setLanguage }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectLanguage = (lang) => {
    setLanguage(lang);
    handleClose();
  };

  return (
    <div className={classes.languageSelector}>
      <IconButton
        onClick={handleClick}
        className={classes.languageButton}
        size="small"
      >
        <Globe size={20} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {Object.entries(languageNames).map(([code, name]) => (
          <MenuItem
            key={code}
            selected={language === code}
            onClick={() => handleSelectLanguage(code)}
          >
            {name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

// Componente principal del Signup
function SignupTree() {
  const classes = useStyles();
  const history = useHistory();
  const { language, setLanguage, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validar que las contraseñas coincidan
    if (formData.password !== formData.confirmPassword) {
      setError(t.signup.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post('/auth/signup', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company: formData.company || undefined,
        phone: formData.phone || undefined,
      });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      history.push('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || t.signup.errorSignup);
      setLoading(false);
    }
  };

  return (
    <div className={classes.container}>
      {/* Selector de idioma */}
      <LanguageSelector language={language} setLanguage={setLanguage} />

      {/* Canvas 3D de fondo con Neural Brain Network */}
      <div className={classes.canvas}>
        <NeuralBrain3D />
      </div>

      {/* Contenido del formulario */}
      <div className={classes.content}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className={classes.signupCard}>
            <div className={classes.header}>
              <div className={classes.logo}>
                <div className={classes.logoIcon}>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="8" r="3" fill="white" opacity="0.9" />
                    <circle cx="8" cy="16" r="2.5" fill="white" opacity="0.7" />
                    <circle cx="24" cy="16" r="2.5" fill="white" opacity="0.7" />
                    <circle cx="12" cy="24" r="2" fill="white" opacity="0.6" />
                    <circle cx="20" cy="24" r="2" fill="white" opacity="0.6" />
                    <line x1="16" y1="11" x2="8" y2="14" stroke="white" strokeWidth="1.5" opacity="0.5" />
                    <line x1="16" y1="11" x2="24" y2="14" stroke="white" strokeWidth="1.5" opacity="0.5" />
                    <line x1="8" y1="18" x2="12" y2="22" stroke="white" strokeWidth="1.5" opacity="0.4" />
                    <line x1="24" y1="18" x2="20" y2="22" stroke="white" strokeWidth="1.5" opacity="0.4" />
                  </svg>
                </div>
              </div>
              <h1 className={classes.title}>{t.signup.title}</h1>
              <p className={classes.subtitle}>{t.signup.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className={classes.form}>
              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.signup.name}
                  variant="outlined"
                  type="text"
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <User size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.signup.email}
                  variant="outlined"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <Mail size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.signup.password}
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <Lock size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
                <div
                  className={classes.eyeIcon}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </div>
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.signup.confirmPassword}
                  variant="outlined"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  required
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <Lock size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
                <div
                  className={classes.eyeIcon}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </div>
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.signup.company}
                  variant="outlined"
                  type="text"
                  value={formData.company}
                  onChange={handleChange('company')}
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <Building size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
              </div>

              <div className={classes.inputWrapper}>
                <TextField
                  fullWidth
                  label={t.signup.phone}
                  variant="outlined"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  className={classes.input}
                  InputProps={{
                    startAdornment: (
                      <Phone size={20} style={{ marginRight: 8, color: 'rgba(0, 217, 255, 0.7)' }} />
                    ),
                  }}
                />
              </div>

              {error && (
                <Alert severity="error" className={classes.alert}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                className={classes.button}
              >
                {loading ? (
                  <CircularProgress size={24} style={{ color: '#000000' }} />
                ) : (
                  t.signup.signupButton
                )}
              </Button>
            </form>

            <div className={classes.footer}>
              {t.signup.hasAccount}{' '}
              <a href="/login" className={classes.link}>
                {t.signup.loginLink}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default SignupTree;
